import api, { getStoredUser, unwrapSingle } from "@/lib/api";
import { getIncident } from "@/services/incidentService";
import { getLocationCoords } from "@/lib/geocode";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function cleanText(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}

function formatCoord(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n.toFixed(5) : cleanText(value);
}

/** Turn a location (string | {address,city,...} | {lat,lon}) into display text. Never returns "[object Object]". */
export function resolveIncidentLocation(loc) {
  if (!loc) return "";
  if (typeof loc === "string") return loc.trim();
  if (typeof loc !== "object") return cleanText(loc);

  const parts = [loc.address, loc.city, loc.district, loc.state]
    .map((part) => (part && typeof part === "object" ? resolveIncidentLocation(part) : cleanText(part)))
    .filter(Boolean);
  if (parts.length) return parts.join(", ");

  const lat = loc.lat ?? loc.latitude;
  const lon = loc.lon ?? loc.lng ?? loc.longitude;
  if (lat !== undefined && lat !== null && lon !== undefined && lon !== null) {
    return `${formatCoord(lat)}, ${formatCoord(lon)}`;
  }
  return "";
}

/** Registration plate from any incident/WS payload shape (never a UUID). */
export function extractIncidentPlate(inc) {
  if (!inc || typeof inc !== "object") return "";
  const ok = (v) => {
    const s = cleanText(v);
    return s && !UUID_RE.test(s) ? s : "";
  };

  const primary = Array.isArray(inc.vehicles)
    ? inc.vehicles.find((v) => v?.role === "PRIMARY") || inc.vehicles[0]
    : null;

  return (
    ok(primary?.registration_number)
    || ok(inc.vehicle?.registration_number)
    || ok(inc.registration_number)
    || ok(inc.vehicle_number)
    || ok(inc.plate)
    || ""
  );
}

function buildDetails(inc, plate) {
  const details = [];
  const push = (label, value) => {
    const v = cleanText(value);
    if (v) details.push({ label, value: v });
  };
  push("Registration Number", plate);
  push("Severity", String(inc.severity || "").toUpperCase());
  push("Status", inc.status);
  push("Type", inc.incident_type || inc.type);
  push("Driver", inc.driver?.name || inc.driver_name);
  push("Description", inc.description);
  return details;
}

/** True when the current user's org already posted a service response (shared `status` is not per-org). */
export function isAlertHandledByMyOrg(alert) {
  const orgType = getStoredUser()?.org_type;
  return Boolean(orgType && alert?.service_statuses?.[orgType]);
}

/**
 * Build the object consumed by EmergencyAlertModal from a (usually minimal) WS incident payload.
 * Fetches the full incident (vehicles, driver, location) so the popup is populated.
 */
export async function buildIncidentAlert(raw, { fallbackPlate = "", fallbackLocation = "" } = {}) {
  const base = raw?.incident || raw?.payload || raw?.data || raw || {};
  const incidentId = base.incident_id || base.incidentId || base.id || null;
  const primaryFromBase = Array.isArray(base.vehicles)
    ? base.vehicles.find((v) => v?.role === "PRIMARY") || base.vehicles[0]
    : null;
  const vehicleId = base.vehicle_id || primaryFromBase?.vehicle_id || null;

  // WS payload is minimal ({incident_id, vehicle_id, incident_type, severity, location, event_time}).
  // Load the full incident and the vehicle in parallel to resolve plate, driver, owner, etc.
  const [incResult, vehicleResult] = await Promise.allSettled([
    incidentId ? getIncident(incidentId) : Promise.resolve(null),
    vehicleId ? api.get(`/vehicles/${vehicleId}`).then(unwrapSingle) : Promise.resolve(null),
  ]);

  const full = incResult.status === "fulfilled" && incResult.value && typeof incResult.value === "object" ? incResult.value : {};
  const vehicle = vehicleResult.status === "fulfilled" && vehicleResult.value && typeof vehicleResult.value === "object" ? vehicleResult.value : null;
  if (incResult.status === "rejected") console.error("[incidentAlert] Failed to fetch incident:", incResult.reason);

  const inc = { ...base, ...full };
  const plate = extractIncidentPlate(inc) || extractIncidentPlate(vehicle) || fallbackPlate || "";
  const coords = getLocationCoords(inc.location) || getLocationCoords(base.location);
  const addressText = resolveIncidentLocation(inc.location);
  const driver = inc.driver || vehicle?.driver || null;
  const vehicleModel = [vehicle?.make, vehicle?.model].filter(Boolean).join(" ");
  const ownerArea = [vehicle?.city, vehicle?.district, vehicle?.state].filter((v) => v && v !== "NA").join(", ");

  const info = {
    incidentId,
    vehicleId,
    type: inc.incident_type || inc.type || "INCIDENT",
    severity: String(inc.severity || "high").toLowerCase(),
    status: String(inc.status || "OPEN").toUpperCase(),
    eventTime: inc.event_time || inc.timestamp || inc.created_at || null,
    coords,
    addressText,
    plate,
    vehicleModel,
    vehicleCategory: vehicle?.vehicle_category || "",
    vehicleStatus: vehicle?.status || "",
    ownerArea,
    driverName: cleanText(driver?.name),
    driverPhone: cleanText(driver?.phone),
    description: cleanText(inc.description),
    serviceStatuses: inc.service_statuses || {},
  };

  return {
    incidentId,
    type: info.type,
    location: addressText || fallbackLocation || (coords ? `${coords.lat.toFixed(5)}, ${coords.lon.toFixed(5)}` : "Location unavailable"),
    severity: info.severity,
    service_statuses: info.serviceStatuses,
    vehicles: inc.vehicles,
    vehicle: inc.vehicle,
    registration_number: plate || undefined,
    vehicle_number: inc.vehicle_number,
    timestamp: info.eventTime || new Date().toISOString(),
    status: info.status,
    description: info.description,
    details: buildDetails(inc, plate),
    info,
  };
}
